import Cookies from "js-cookie";
const KETCH_COOKIE_NAME = "_ketch_consent_v1_";
// Adapted from Ketch's exported code, accessible in the Ketch platform
// (https://app.ketch.com/deployment/applications/view/singlestore_portal)
export function initKetch(applicationCode) {
    window.semaphore = window.semaphore || [];
    window.ketch = function () {
        // @ts-ignore logic copied from ketch js snippet
        window.semaphore.push(arguments);
    };
    const search = new URLSearchParams(document.location.search);
    const application = search.get("property") ?? applicationCode;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = `https://global.ketchcdn.com/web/v2/config/singlestore/${application}/boot.js`;
    script.defer = script.async = true;
    const style = document.createElement("style");
    // Adjusts Ketch's dialog to limit its width
    style.textContent = `
    #lanyard_root div[class*="bottom"] > div[role="dialog"] {
        max-width: 1300px;
        margin-inline: auto;
        padding-bottom: var(--sui-size-1x);
    }
    `;
    const head = document.getElementsByTagName("head")[0];
    head.prepend(script); //insert at top
    head.appendChild(style);
}
const validateConsentCookie = (consent, logger) => {
    if (consent.essential === undefined) {
        logger?.logError(new Error(`Expected "essential" key is missing in ${KETCH_COOKIE_NAME}`));
    }
};
export const getUserConsent = (logger) => {
    const cookie = Cookies.get(KETCH_COOKIE_NAME);
    let consent = {};
    if (cookie) {
        try {
            // Decode consent info from base64
            const consentObj = JSON.parse(atob(decodeURIComponent(cookie)));
            // simplify structure to {purpose: true|false}
            consent = Object.fromEntries(Object.entries(consentObj).map(([purpose, info]) => {
                return [purpose, info.status === "granted"];
            }));
            validateConsentCookie(consent, logger);
        }
        catch {
            logger?.logError(new Error(`Failed to decode ${KETCH_COOKIE_NAME} cookie`));
        }
    }
    else {
        logger?.logError(new Error(`Missing ${KETCH_COOKIE_NAME} cookie`));
    }
    return consent;
};
export const isConsentDenyAll = (consent) => {
    for (const purpose in consent) {
        const consentValue = consent[purpose];
        if (purpose !== "essential" && consentValue) {
            return false;
        }
    }
    return true;
};
// Remove cookies that are not allowed by the user
// (only first-party cookies are removed)
export const removeDisallowedCookies = async () => {
    const consent = getUserConsent();
    if (window?.ketch === undefined) {
        return null;
    }
    // The purpose list is only available locally when you register a plugin
    window.ketch("registerPlugin", (_, { purposes }) => {
        purposes.forEach(({ code, cookies }) => {
            if (!consent[code]) {
                // Delete all cookies from that purpose
                cookies.forEach((cookie) => {
                    Cookies.remove(cookie.name, { domain: cookie.host });
                });
            }
        });
    });
};
export const registerConsentListener = (callback) => {
    window.semaphore = window.semaphore || [];
    window.semaphore.push([
        "onConsent",
        (rawConsent) => {
            const consent = rawConsent?.purposes || {};
            callback(consent);
        },
    ]);
};
// Triggered only when the user dismisses the modal,
// it is not triggered when the user saves his preferences
// (applicable if the page uses the modal instead of the banner)
export const registerHideExperienceListener = (callback) => {
    if (window.ketch) {
        window.ketch("on", "hideExperience", callback);
    }
};
// Resolves when either the modal is dismissed (first visit), or the consent is set
export const waitForInitialConsent = async () => {
    return new Promise((resolve) => {
        registerHideExperienceListener(resolve);
        registerConsentListener((consent) => {
            // The first time "registerConsentListener" is triggered is on the page load
            // in that situation the purpose object only contains the "essential" key
            // we ignore that case since the user hasn't given/declined consent yet
            if (Object.keys(consent).length > 1) {
                resolve();
            }
        });
    });
};
export const initializeDataLayer = () => {
    window.dataLayer = window.dataLayer || [];
    window.semaphore = window.semaphore || [];
    const gtag = (...params) => {
        window.dataLayer.push(params);
    };
    gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
        functionality_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted",
        wait_for_update: 2000,
    });
    gtag("set", "ads_data_redaction", true);
    gtag("set", "url_passthrough", true);
    // Update GTM consent from Ketch's purposes
    registerConsentListener((consent) => {
        gtag("consent", "update", {
            ad_storage: consent.advertising ? "granted" : "denied",
            ad_user_data: consent.advertising ? "granted" : "denied",
            ad_personalization: consent.advertising ? "granted" : "denied",
            analytics_storage: consent.analytics_performance_and_customization
                ? "granted"
                : "denied",
            functionality_storage: consent.functionality ? "granted" : "denied",
            personalization_storage: consent.analytics_performance_and_customization
                ? "granted"
                : "denied",
            security_storage: "granted",
            wait_for_update: 2000,
        });
        window.dataLayer.push({ event: "cookie_consent_update" });
    });
};
export const openPrivacyCenter = () => {
    window.semaphore = window.semaphore || [];
    window.semaphore.push(["showPreferences"]);
};
//# sourceMappingURL=ketch.js.map